import { Injectable } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { MenuItemCommandEvent } from "primeng/api";
import { BehaviorSubject, from, map, mergeMap, Observable, of, shareReplay, Subject, switchMap, tap } from "rxjs";
import { environment } from "../../../../../environments/environment";
import { ExtraServiceStateKind, RanExpenseAccessKind } from "../../../../domain/extra-service";
import { OrganizationDetails } from "../../../../domain/organization";
import { UserDetails } from "../../../../domain/user";
import { FeatureFlagService } from "../../../../services/logical/feature-flag.service";
import {
  AppMenuAsyncNavigableBranch,
  AppMenuItem,
  AppMenuLeaf,
  isAppMenuAsyncNavigableBranch,
  isAppMenuBranch,
  isAppMenuNavigableBranch,
} from "../components/app.menu.types";

@Injectable({
  providedIn: "root",
})
export class AppNavMenuService {
  private _loadingPlaceholder: AppMenuLeaf[] = [
    {
      id: "loader",
      label: "",
    },
  ];

  private notificationCountSubjects = new Map<string, BehaviorSubject<number>>();

  constructor(
    private readonly translocoService: TranslocoService,
    private readonly featureFlagService: FeatureFlagService,
  ) {
    this._loadingPlaceholder[0].label = this.translocoService.translate("app.sidebar.loadingPlaceholder");
  }

  public getAdminNavModel(): Observable<AppMenuItem[]> {
    return this.translocoService.selectTranslation().pipe(
      map(() => {
        return [
          {
            label: this.translocoService.translate("app.sidebar.sectionTitles.home"),
            items: [
              {
                label: this.translocoService.translate("app.sidebar.pages.home"),
                icon: "pi pi-fw pi-home",
                routerLink: ["/admin/home"],
              },
            ],
          },
          {
            label: this.translocoService.translate("app.sidebar.sectionTitles.processes"),
            items: [
              {
                label: this.translocoService.translate("app.sidebar.pages.processes"),
                icon: "pi pi-fw pi-cog",
                routerLink: ["/admin/processes"],
              },
              {
                label: this.translocoService.translate("app.sidebar.pages.newProcess"),
                icon: "pi pi-fw pi-plus",
                routerLink: ["/admin/processes/new"],
              },
            ],
          },
          {
            label: this.translocoService.translate("app.sidebar.sectionTitles.features"),
            items: [
              {
                label: this.translocoService.translate("app.sidebar.pages.features"),
                icon: "pi pi-fw pi-bookmark",
                routerLink: ["/admin/features"],
              },
              {
                label: this.translocoService.translate("app.sidebar.pages.newFeature"),
                icon: "pi pi-fw pi-plus",
                routerLink: ["/admin/features/new"],
              },
            ],
          },
          {
            label: this.translocoService.translate("app.sidebar.sectionTitles.services"),
            items: [
              {
                label: this.translocoService.translate("app.sidebar.pages.services"),
                icon: "pi pi-fw pi-list",
                routerLink: ["/admin/services"],
              },
            ],
          },
          {
            label: this.translocoService.translate("app.sidebar.sectionTitles.organizations"),
            items: [
              {
                label: this.translocoService.translate("app.sidebar.pages.organizations"),
                icon: "pi pi-fw pi-building",
                routerLink: ["/admin/organizations"],
              },
            ],
          },
          {
            label: this.translocoService.translate("app.sidebar.sectionTitles.users"),
            items: [
              {
                label: this.translocoService.translate("app.sidebar.pages.users"),
                icon: "pi pi-fw pi-users",
                routerLink: ["/admin/users"],
              },
            ],
          },
        ];
      }),
      shareReplay(1),
    );
  }

  public getUserNavModel(activeOrg: OrganizationDetails, currentUser: UserDetails): Observable<AppMenuItem[]> {
    const orgId = activeOrg.id.toString();
    const userAccessRole =
      currentUser.roleAssignments.find((ra) => ra.organizationId === activeOrg.id)?.accessRole ?? null;
    const isInternal = currentUser.isInternal === true;

    const canSeeInfo = isInternal || (userAccessRole?.hasAny(["canSeeResponsiblePersons"]) ?? false);
    const canSeeSettings =
      isInternal ||
      (userAccessRole?.hasAny([
        "canModifyAccessRoles",
        "canModifyApprovalGroups",
        "canModifyInvitations",
        "canModifyUsers",
        "canRemoveUsers",
        "canModifyAutomationUsers",
      ]) ??
        false);
    // TODO(extra-services): remove the feature flag check once the feature is released. Page is still accessible by URL
    const canSeeExtraServices =
      this.featureFlagService.isEnabled("extraServices") &&
      (isInternal ||
        (userAccessRole?.hasAny(["canViewExtraServices", "canViewAllServices", "canModifyExtraServices"]) ?? false));
    const canSeeAuditLog = isInternal || (userAccessRole?.hasAny(["canViewAuditLog"]) ?? false);
    const shouldShowOrgSection = canSeeInfo || canSeeSettings || canSeeExtraServices || canSeeAuditLog;

    // Link shows only when RAN Expense is activated for the org and this user's role grants access.
    const ranExpense = activeOrg.extraServices.find(
      (s) => s.isRanExpense && s.state !== ExtraServiceStateKind.Inactive,
    );
    const canSeeRanExpense =
      ranExpense !== undefined &&
      (isInternal || (userAccessRole?.ranExpenseAccess ?? RanExpenseAccessKind.None) !== RanExpenseAccessKind.None);
    const ranExpenseIcon = `pi pi-fw ${ranExpense?.iconString ?? "pi-money-bill"}`;

    return this.translocoService.selectTranslation().pipe(
      map(() => {
        const menuItems: AppMenuItem[] = [
          {
            label: this.translocoService.translate("app.sidebar.sectionTitles.home"),
            items: [
              {
                label: this.translocoService.translate("app.sidebar.pages.home"),
                icon: "pi pi-fw pi-home",
                routerLink: ["/organizations", orgId],
              },
              ...(canSeeRanExpense
                ? [
                    {
                      label: this.translocoService.translate("app.sidebar.pages.expenseManagement"),
                      icon: ranExpenseIcon,
                      url: environment.ranExpense.url,
                      target: "_blank",
                    },
                  ]
                : []),
            ],
          },
          {
            label: this.translocoService.translate("app.sidebar.sectionTitles.processes"),
            items: [
              {
                id: "allProcesses",
                label: this.translocoService.translate("app.sidebar.pages.allProcesses"),
                icon: "pi pi-fw pi-list",
                routerLink: ["/organizations", orgId, "transactions"],
                notificationCount: 0,
              },
              {
                label: this.translocoService.translate("app.sidebar.pages.uploadedFiles"),
                icon: "pi pi-fw pi-paperclip",
                routerLink: ["/organizations", orgId, "transactions", "uploaded-files"],
              },
              {
                label: this.translocoService.translate("app.sidebar.pages.startNewProcess"),
                icon: "pi pi-fw pi-plus",
                routerLink: ["/organizations", orgId, "processes"],
              },
            ],
          },
        ];

        if (shouldShowOrgSection) {
          const orgSectionItems: AppMenuItem[] = [];
          if (canSeeInfo) {
            orgSectionItems.push({
              label: this.translocoService.translate("app.sidebar.pages.info"),
              icon: "pi pi-info-circle",
              routerLink: ["/organizations", orgId, "info"],
            });
          }
          if (canSeeSettings) {
            orgSectionItems.push({
              label: this.translocoService.translate("app.sidebar.pages.settings"),
              icon: "pi pi-cog",
              routerLink: ["/organizations", orgId, "settings"],
            });
          }
          if (canSeeExtraServices) {
            orgSectionItems.push({
              label: this.translocoService.translate("app.sidebar.pages.extraServices"),
              icon: "pi pi-shopping-cart",
              routerLink: ["/organizations", orgId, "services"],
              routerLinkActiveOptions: {
                paths: "subset",
                queryParams: "ignored",
                matrixParams: "ignored",
                fragment: "ignored",
              },
            });
          }
          if (canSeeAuditLog) {
            orgSectionItems.push({
              label: this.translocoService.translate("app.sidebar.pages.auditLog"),
              icon: "pi pi-history",
              routerLink: ["/organizations", orgId, "audit-log"],
            });
          }
          menuItems.push({
            label: this.translocoService.translate("app.sidebar.sectionTitles.organization"),
            items: orgSectionItems,
          });
        }

        return menuItems;
      }),
      shareReplay(1),
    );
  }

  private loadBranch(branch: AppMenuAsyncNavigableBranch): Observable<AppMenuAsyncNavigableBranch> {
    if (branch.state.isLoaded) {
      return of(branch);
    }

    const completion = new Subject<AppMenuAsyncNavigableBranch>();
    branch.state
      .loadItems(branch)
      .pipe(
        tap((x) => {
          branch.state.isLoaded = true;
          branch.items = x;
        }),
        switchMap(() => of(branch)),
      )
      .subscribe((x) => {
        completion.next(x);
      });

    return completion;
  }

  public loadItemForUrl(items: AppMenuItem[], url: string, depth: number): Observable<AppMenuLeaf | null> {
    // First, see if there are any that trivially match
    const directMatch = items.find((item) => this.isMatchingLeafRoute(item, url)) ?? null;
    if (directMatch) {
      return of(directMatch);
    }

    // Calculate expected URL prefix based on current recursion depth
    const currentSlashCount = 1 + depth + 1; // An extra + 1 to skip the initial slash of the home page.
    const indexOfCurrentSlash = this.nthIndexOf(url, "/", currentSlashCount);
    if (indexOfCurrentSlash === -1) {
      return of(null);
    }

    const urlToMatch = url.substring(0, indexOfCurrentSlash);

    // If a branch entry has *no* URL prefix, one of its children *might* match
    // If it DOES have a url prefix, we can decisively say whether or not it matches
    const definiteMatch = items.filter(isAppMenuNavigableBranch).find((x) => x.state.urlPrefix === urlToMatch);
    const possibleMatches = items.filter(isAppMenuBranch);

    if (!definiteMatch && possibleMatches?.length > 0) {
      return from(possibleMatches).pipe(mergeMap((x) => this.loadItemForUrl(x.items, url, depth)));
    }

    if (!definiteMatch) {
      return of(null);
    }

    if (isAppMenuAsyncNavigableBranch(definiteMatch)) {
      return this.loadBranch(definiteMatch).pipe(
        switchMap((x) => {
          return this.loadItemForUrl(x.items, url, depth + 1);
        }),
      );
    } else {
      return this.loadItemForUrl(definiteMatch.items, url, depth + 1);
    }
  }

  private nthIndexOf(search: string, element: string, n: number): number {
    const length = search.length;
    let i = -1;
    while (n-- && i++ < length) {
      i = search.indexOf(element, i);
      if (i < 0) {
        break;
      }
    }
    return i;
  }

  private isMatchingLeafRoute(item: AppMenuItem, url: string): item is AppMenuLeaf {
    const itemUrl = this.getMenuItemUrl(item);
    if (!itemUrl) {
      return false;
    }

    const normalizedUrl = this.normalizeUrl(url);
    const normalizedItemUrl = this.normalizeUrl(itemUrl);
    const usesSubsetMatch = item.routerLinkActiveOptions?.paths === "subset";

    return (
      normalizedUrl === normalizedItemUrl || (usesSubsetMatch && normalizedUrl.startsWith(`${normalizedItemUrl}/`))
    );
  }

  private getMenuItemUrl(item: AppMenuLeaf): string | null {
    if (!item.routerLink || item.routerLink.length === 0) {
      return null;
    }

    const normalizedSegments = item.routerLink
      .map((segment, index) => {
        const stringSegment = String(segment);
        return index === 0 ? stringSegment.replace(/\/+$/g, "") : stringSegment.replace(/^\/+|\/+$/g, "");
      })
      .filter((segment) => segment.length > 0);

    if (normalizedSegments.length === 0) {
      return "/";
    }

    const joinedPath = normalizedSegments.join("/");
    return joinedPath.startsWith("/") ? joinedPath : `/${joinedPath}`;
  }

  private normalizeUrl(url: string): string {
    const [pathWithoutQueryOrFragment] = url.split(/[?#]/, 1);
    if (!pathWithoutQueryOrFragment || pathWithoutQueryOrFragment === "/") {
      return "/";
    }

    return pathWithoutQueryOrFragment.endsWith("/")
      ? pathWithoutQueryOrFragment.slice(0, -1)
      : pathWithoutQueryOrFragment;
  }

  private branchClicked(event: MenuItemCommandEvent): void {
    if (event.item === undefined || !isAppMenuAsyncNavigableBranch(event.item)) {
      return;
    }

    this.loadBranch(event.item);
  }

  private createAsyncBranch(
    label: string,
    icon: string,
    urlPrefix: string,
    loadCallback: (parent: AppMenuAsyncNavigableBranch) => Observable<AppMenuItem[]>,
  ): AppMenuAsyncNavigableBranch {
    return {
      label,
      icon,
      command: (e) => this.branchClicked(e),
      state: { isLoaded: false, loadItems: loadCallback, urlPrefix: urlPrefix },
      items: this._loadingPlaceholder,
    };
  }
}
